
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

type SoundName = 'putonboard' | 'results' | 'correct' | 'incorrect';

interface SoundContextType {
  isSoundEnabled: boolean;
  toggleSound: () => void;
  playSound: (soundName: SoundName) => void;
}

const SoundContext = createContext<SoundContextType | undefined>(undefined);

// Sound file paths with fallbacks
const SOUND_PATHS: { [key in SoundName]: string[] } = {
  putonboard: ['/sounds/putonboard_converted.wav', '/sounds/putonboard.wav'],
  results: ['/sounds/results_converted.wav', '/sounds/results.wav'],
  correct: ['/sounds/correct_converted.wav', '/sounds/correct.wav'],
  incorrect: ['/sounds/incorrect_converted.wav', '/sounds/incorrect.wav'],
};

export const SoundProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [preloadedAudio, setPreloadedAudio] = useState<{ [key in SoundName]?: HTMLAudioElement }>({});
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const storedValue = localStorage.getItem('soundEnabled');
    if (storedValue !== null) {
      const enabled = JSON.parse(storedValue);
      console.log('Loading sound preference from localStorage:', enabled);
      setIsSoundEnabled(enabled);
    } else {
      console.log('No sound preference in localStorage, defaulting to enabled');
    }
  }, []);

  const toggleSound = useCallback(() => {
    setIsSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('soundEnabled', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // Preload audio files
  const preloadAudioFiles = useCallback(() => {
    console.log('Preloading audio files...');
    const loadedAudio: { [key in SoundName]?: HTMLAudioElement } = {};
    
    Object.entries(SOUND_PATHS).forEach(([soundName, paths]) => {
      const tryLoadPath = (pathIndex: number) => {
        if (pathIndex >= paths.length) {
          console.warn(`Failed to load all paths for sound: ${soundName}`);
          return;
        }
        
        const audio = new Audio();
        audio.preload = 'auto';
        audio.volume = 1;
        
        const onCanPlay = () => {
          console.log(`Successfully preloaded: ${soundName} from ${paths[pathIndex]}`);
          loadedAudio[soundName as SoundName] = audio;
          setPreloadedAudio(prev => ({ ...prev, [soundName]: audio }));
          audio.removeEventListener('canplaythrough', onCanPlay);
          audio.removeEventListener('error', onError);
        };
        
        const onError = () => {
          console.log(`Failed to preload ${soundName} from ${paths[pathIndex]}, trying next...`);
          audio.removeEventListener('canplaythrough', onCanPlay);
          audio.removeEventListener('error', onError);
          tryLoadPath(pathIndex + 1);
        };
        
        audio.addEventListener('canplaythrough', onCanPlay);
        audio.addEventListener('error', onError);
        audio.src = paths[pathIndex];
      };
      
      tryLoadPath(0);
    });
  }, []);

  // Initialize audio context after user interaction
  const initializeAudio = useCallback(() => {
    if (!audioInitialized) {
      console.log('Initializing audio context after user interaction');
      // Create a silent audio to unlock the audio context
      const silentAudio = new Audio();
      silentAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
      silentAudio.volume = 0;
      silentAudio.play().then(() => {
        console.log('Audio context unlocked');
        silentAudio.pause();
        setAudioInitialized(true);
        // Start preloading audio files after unlocking audio context
        preloadAudioFiles();
      }).catch(() => {
        console.log('Failed to unlock audio context, but continuing anyway');
        setAudioInitialized(true);
        // Still try to preload files
        preloadAudioFiles();
      });
    }
  }, [audioInitialized, preloadAudioFiles]);

  // Add click listener to initialize audio
  useEffect(() => {
    const handleFirstInteraction = () => {
      initializeAudio();
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

    if (!audioInitialized) {
      document.addEventListener('click', handleFirstInteraction);
      document.addEventListener('keydown', handleFirstInteraction);
    }

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [audioInitialized, initializeAudio]);

  // Fallback function for when preloaded audio isn't available
  const fallbackPlaySound = useCallback((soundName: SoundName) => {
    const soundPaths = SOUND_PATHS[soundName];
    console.log('Fallback: Trying sound paths:', soundPaths);
    
    const tryPath = (pathIndex: number) => {
      if (pathIndex >= soundPaths.length) {
        console.error('All sound paths failed for:', soundName);
        return;
      }
      
      const soundPath = soundPaths[pathIndex];
      console.log(`Fallback: Trying path ${pathIndex + 1}/${soundPaths.length}:`, soundPath);
      
      const audio = new Audio(soundPath);
      audio.volume = 1;
      
      audio.play().then(() => {
        console.log('Fallback: Successfully playing:', soundPath);
      }).catch(error => {
        console.error(`Fallback: Play failed for: ${soundPath}`, error);
        tryPath(pathIndex + 1);
      });
    };
    
    tryPath(0);
  }, []);

  const playSound = useCallback((soundName: SoundName) => {
    console.log('playSound called:', { soundName, isSoundEnabled, isAuthenticated, audioInitialized });
    
    if (isSoundEnabled && isAuthenticated) {
      // Initialize audio if not done yet
      if (!audioInitialized) {
        initializeAudio();
        return;
      }
      
      // Use preloaded audio if available
      const preloadedAudioElement = preloadedAudio[soundName];
      if (preloadedAudioElement) {
        console.log('Playing preloaded audio for:', soundName);
        // Reset the audio to the beginning
        preloadedAudioElement.currentTime = 0;
        preloadedAudioElement.play().then(() => {
          console.log('Successfully playing preloaded audio:', soundName);
        }).catch(error => {
          console.error(`Failed to play preloaded audio: ${soundName}`, error);
          // Fallback to creating new audio element
          fallbackPlaySound(soundName);
        });
      } else {
        console.log('No preloaded audio found, using fallback for:', soundName);
        fallbackPlaySound(soundName);
      }
    } else {
      console.log('Sound not played because:', { 
        soundEnabled: isSoundEnabled, 
        authenticated: isAuthenticated,
        audioInitialized: audioInitialized
      });
    }
  }, [isSoundEnabled, isAuthenticated, audioInitialized, preloadedAudio, initializeAudio, fallbackPlaySound]);

  return (
    <SoundContext.Provider value={{ isSoundEnabled, toggleSound, playSound }}>
      {children}
    </SoundContext.Provider>
  );
};

export const useSound = () => {
  const context = useContext(SoundContext);
  if (context === undefined) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
};
